import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Megaphone, Plus, Loader2, X, Edit2, Trash2, CheckCircle2, AlertTriangle,
  ShieldCheck, MapPin, DollarSign, Users, Image as ImageIcon, Wand2, Save,
  ChevronRight, Building2, Globe2, FileText, Rocket, RefreshCw, PauseCircle,
  BarChart3, Eye, MousePointerClick, Target, Clock, Activity,
  Mail, Phone, Send, Inbox,
} from 'lucide-react';
import { LintBadge } from './LintBadge';
import ClaimAudit from './ClaimAudit';
import { EmailImagePanel } from './EmailImagePanel';
import PromoteRequests from './PromoteRequests';

const API = '/api';

const OBJECTIVE_OPTIONS = [
  { id: 'lead_gen',   label: 'Lead generation (fitting requests)' },
  { id: 'awareness',  label: 'Local awareness' },
  { id: 'traffic',    label: 'Traffic to fitter profile' },
  { id: 'engagement', label: 'Engagement (likes / shares)' },
];

const ANGLE_PRESETS = [
  'Equipment Affects Motion',
  'The teacher matters',
  'Fit beats brand',
  'Junior golfer focus',
  'Returning player',
  'Senior / longevity focus',
];

const STATUS_BADGE = {
  draft:      { label: 'Draft',     color: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
  approved:   { label: 'Approved',  color: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  scheduled:  { label: 'Scheduled', color: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  running:    { label: 'Running',   color: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  paused:     { label: 'Paused',    color: 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10' },
  completed:  { label: 'Completed', color: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
  rejected:   { label: 'Rejected',  color: 'text-red-400 border-red-500/40 bg-red-500/10' },
};


export default function PromoteFitters({ onNavigate, initialTab } = {}) {
  const [tab, setTab] = useState(initialTab || 'fitters');
  // The dashboard's promote requests row opens this screen on its Requests tab.
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  return (
    <div className="space-y-5" data-testid="promote-fitters-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">PROMOTE FITTERS</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">Paid ad campaigns for HG-certified teacher/fitters — Meta (Facebook + Instagram) Lead Ads, geo-targeted to their local area.</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-hg-green font-sans bg-hg-green/10 border border-hg-green/30 rounded-sm px-2.5 py-1">
          <ShieldCheck size={11} /> Publish paused · activate with a spend cap · live performance + lead capture
        </div>
      </div>

      {/* Tabs — the Leads tab moved out (2026-07-27): Leads Tracker is the one home
          for lead follow-up; the link keeps it one click away. */}
      <div className="flex items-center gap-1 border-b border-hg-border">
        {[
          { id: 'fitters', label: 'Fitter Directory' },
          { id: 'drafts',  label: 'Ad Drafts' },
          // Fitters asking from the website (Randall, 21 Oct 2026); promote_requests.py.
          { id: 'requests', label: 'Requests from fitters' },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`promote-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-sans border-b-2 transition-colors ${
              tab === t.id ? 'border-hg-green text-hg-text' : 'border-transparent text-hg-text-dim hover:text-hg-text'
            }`}
          >{t.label}</button>
        ))}
        {onNavigate && (
          <button
            data-testid="promote-view-leads-link"
            onClick={() => onNavigate('leads-tracker')}
            className="ml-auto px-4 py-2 text-xs font-sans text-hg-green hover:text-hg-green/80 transition-colors font-medium"
          >View leads →</button>
        )}
      </div>

      {tab === 'fitters' && <FitterDirectory />}
      {tab === 'drafts' && <AdDraftsView />}
      {tab === 'requests' && <PromoteRequests onMakeAd={() => setTab('drafts')} />}
    </div>
  );
}


// ===========================================================================
// Fitter Directory
// ===========================================================================

function FitterDirectory() {
  const [fitters, setFitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // a fitter object or {} for new
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/promoted-fitters/list`);
      setFitters(res.data.fitters || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load fitters.');
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const handleDelete = async (fitter) => {
    if (!window.confirm(`Delete ${fitter.name}? All ad drafts for them will also be removed.`)) return;
    try {
      await axios.delete(`${API}/promoted-fitters/${fitter.id}`);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="space-y-3">
      {!editing && (
        <div className="flex justify-end">
          <button
            data-testid="add-fitter-btn"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90"
          ><Plus size={12} /> Add fitter</button>
        </div>
      )}

      {editing && (
        <FitterEditor
          fitter={editing.id ? editing : null}
          onSave={async () => { setEditing(null); await reload(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      {error && (
        <div className="text-xs text-red-400 font-sans" data-testid="fitter-directory-error">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
      ) : fitters.length === 0 ? (
        <div className="text-center py-12 text-xs text-hg-text-dim font-sans" data-testid="fitter-directory-empty">
          No fitters in the directory yet. Add one to start drafting ad campaigns.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fitter-directory-list">
          {fitters.map((f) => <FitterCard key={f.id} fitter={f} onEdit={() => setEditing(f)} onDelete={() => handleDelete(f)} />)}
        </div>
      )}
    </div>
  );
}


function FitterCard({ fitter, onEdit, onDelete }) {
  const loc = [fitter.city, fitter.state, fitter.country !== 'USA' ? fitter.country : ''].filter(Boolean).join(', ');
  return (
    <div data-testid={`fitter-card-${fitter.id}`} className={`bg-hg-surface border border-hg-border rounded-sm p-4 ${fitter.active ? '' : 'opacity-60'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-sans text-hg-text font-medium truncate">{fitter.name}</h3>
            {fitter.consent_to_use_photo && (
              <span className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-green bg-hg-green/10 border border-hg-green/30 rounded-sm px-1.5 py-0.5"><ShieldCheck size={10} /> photo OK</span>
            )}
            {!fitter.active && (
              <span className="text-[10px] font-sans text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5">inactive</span>
            )}
          </div>
          {fitter.title && <div className="text-[11px] font-sans text-hg-text-dim mt-0.5">{fitter.title}</div>}
          {fitter.facility_name && <div className="text-xs font-sans text-hg-text mt-1.5 flex items-center gap-1"><Building2 size={11} strokeWidth={1.5} /> {fitter.facility_name}</div>}
          {loc && <div className="text-[11px] font-sans text-hg-text-dim mt-0.5 flex items-center gap-1"><MapPin size={10} strokeWidth={1.5} /> {loc}</div>}
          <div className="flex items-center gap-3 mt-2 text-[10px] font-sans text-hg-text-dim">
            <span className="inline-flex items-center gap-1"><Globe2 size={10} strokeWidth={1.5} /> {fitter.default_radius_miles} mi radius</span>
            <span className="inline-flex items-center gap-1"><DollarSign size={10} strokeWidth={1.5} /> ${(fitter.default_monthly_budget_usd || 0).toLocaleString()}/mo default</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            data-testid={`fitter-edit-${fitter.id}`}
            onClick={onEdit}
            className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green"
            title="Edit"
          ><Edit2 size={12} strokeWidth={1.5} /></button>
          <button
            data-testid={`fitter-delete-${fitter.id}`}
            onClick={onDelete}
            className="p-1.5 border border-hg-border rounded-sm text-red-400/70 hover:border-red-400 hover:text-red-400"
            title="Delete"
          ><Trash2 size={12} strokeWidth={1.5} /></button>
        </div>
      </div>
    </div>
  );
}


function FitterEditor({ fitter, onSave, onCancel }) {
  const [form, setForm] = useState(fitter || {
    name: '', title: '', email: '', phone: '',
    facility_name: '', address_line1: '', city: '', state: '', postal_code: '', country: 'USA',
    photo_url: '', bio: '', notes: '',
    consent_to_use_photo: false, consent_to_use_name: true, consent_notes: '',
    default_radius_miles: 25, default_monthly_budget_usd: 0,
    active: true, campaigns: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Campaigns sub-form (additive). Records predating this array have no
  // `campaigns`, so default to [] for editing.
  const campaigns = form.campaigns || [];
  const addCampaign = () => set('campaigns', [
    ...campaigns,
    { label: '', meta_campaign_id: '', meta_ad_set_id: '', meta_lead_form_id: '', spend: null, status: 'active' },
  ]);
  const setCampaign = (i, k, v) => set('campaigns', campaigns.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const removeCampaign = (i) => set('campaigns', campaigns.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name?.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (fitter?.id) {
        await axios.patch(`${API}/promoted-fitters/${fitter.id}`, form);
      } else {
        await axios.post(`${API}/promoted-fitters/add`, form);
      }
      await onSave();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div data-testid="fitter-editor" className="bg-hg-surface border border-hg-green/30 rounded-sm p-4 space-y-3">
      <h3 className="text-xs font-sans text-hg-text uppercase tracking-wider">
        {fitter ? `Edit ${fitter.name}` : 'Add a fitter'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Full name *" testid="fitter-name" value={form.name} onChange={(v) => set('name', v)} />
        <FormField label="Title" testid="fitter-title" value={form.title} onChange={(v) => set('title', v)} placeholder="H-G Master Golf Clubfitter" />
        <FormField label="Email" testid="fitter-email" value={form.email} onChange={(v) => set('email', v)} type="email" />
        <FormField label="Phone" testid="fitter-phone" value={form.phone} onChange={(v) => set('phone', v)} />
        <FormField label="Facility / Academy" testid="fitter-facility" value={form.facility_name} onChange={(v) => set('facility_name', v)} placeholder="Los Lagos Golf Course" />
        <FormField label="Address" testid="fitter-address" value={form.address_line1} onChange={(v) => set('address_line1', v)} />
        <FormField label="City" testid="fitter-city" value={form.city} onChange={(v) => set('city', v)} />
        <FormField label="State / Province" testid="fitter-state" value={form.state} onChange={(v) => set('state', v)} />
        <FormField label="Postal code" testid="fitter-zip" value={form.postal_code} onChange={(v) => set('postal_code', v)} />
        <FormField label="Country" testid="fitter-country" value={form.country} onChange={(v) => set('country', v)} />
        <FormField label="Photo URL" testid="fitter-photo-url" value={form.photo_url} onChange={(v) => set('photo_url', v)} placeholder="https://..." />
        <FormField label="Default ad radius (miles)" testid="fitter-radius" type="number" value={form.default_radius_miles} onChange={(v) => set('default_radius_miles', parseInt(v, 10) || 25)} />
        <FormField label="Default monthly budget (USD)" testid="fitter-budget" type="number" value={form.default_monthly_budget_usd} onChange={(v) => set('default_monthly_budget_usd', parseFloat(v) || 0)} />
      </div>
      <div>
        <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Bio (used in ad context — kept short)</label>
        <textarea
          data-testid="fitter-bio"
          value={form.bio || ''}
          onChange={(e) => set('bio', e.target.value)}
          rows={3}
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
          placeholder="One or two sentences. Their teaching philosophy or what their players know them for."
        />
      </div>

      <div>
        <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Notes (internal — separate from consent notes)</label>
        <textarea
          data-testid="fitter-notes"
          value={form.notes || ''}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
          placeholder="Internal notes about this fitter / their promotion."
        />
      </div>

      <div className="space-y-2 border-t border-hg-border pt-3">
        <p className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Consent on file</p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="fitter-consent-photo"
            checked={!!form.consent_to_use_photo}
            onChange={(e) => set('consent_to_use_photo', e.target.checked)}
            className="mt-0.5 accent-hg-green"
          />
          <span className="text-xs font-sans text-hg-text">Fitter has consented to use of their photo in paid ads.</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="fitter-consent-name"
            checked={!!form.consent_to_use_name}
            onChange={(e) => set('consent_to_use_name', e.target.checked)}
            className="mt-0.5 accent-hg-green"
          />
          <span className="text-xs font-sans text-hg-text">Fitter has consented to use of their name in paid ads.</span>
        </label>
        <FormField label="Consent notes (e.g., signed PDF on file)" testid="fitter-consent-notes" value={form.consent_notes} onChange={(v) => set('consent_notes', v)} />
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="fitter-active"
            checked={!!form.active}
            onChange={(e) => set('active', e.target.checked)}
            className="mt-0.5 accent-hg-green"
          />
          <span className="text-xs font-sans text-hg-text">Active — eligible for new campaigns</span>
        </label>
      </div>

      <div className="space-y-2 border-t border-hg-border pt-3" data-testid="fitter-campaigns">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Campaigns (Meta Lead Ads)</p>
          <button
            type="button"
            data-testid="add-campaign-btn"
            onClick={addCampaign}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
          ><Plus size={10} /> Add campaign</button>
        </div>
        {campaigns.length === 0 ? (
          <p className="text-[11px] text-hg-text-dim font-sans">No campaigns yet. One per promoted fitter to start; add more later.</p>
        ) : (
          campaigns.map((c, i) => (
            <div key={i} data-testid={`campaign-row-${i}`} className="bg-hg-bg/60 border border-hg-border rounded-sm p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Campaign {i + 1}</span>
                <button
                  type="button"
                  data-testid={`remove-campaign-${i}`}
                  onClick={() => removeCampaign(i)}
                  className="inline-flex items-center gap-1 text-[10px] text-hg-text-dim hover:text-red-400 font-sans"
                ><Trash2 size={10} /> Remove</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <FormField label="Label" testid={`campaign-${i}-label`} value={c.label} onChange={(v) => setCampaign(i, 'label', v)} placeholder="e.g. Summer fitting push" />
                <div>
                  <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Status</label>
                  <select
                    data-testid={`campaign-${i}-status`}
                    value={c.status || 'active'}
                    onChange={(e) => setCampaign(i, 'status', e.target.value)}
                    className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                  >
                    <option value="active">active</option>
                    <option value="paused">paused</option>
                  </select>
                </div>
                <FormField label="Meta campaign ID" testid={`campaign-${i}-campaign-id`} value={c.meta_campaign_id} onChange={(v) => setCampaign(i, 'meta_campaign_id', v)} placeholder="120xxxxxxxxxxxxxxx" />
                <FormField label="Meta ad set ID" testid={`campaign-${i}-adset-id`} value={c.meta_ad_set_id} onChange={(v) => setCampaign(i, 'meta_ad_set_id', v)} placeholder="120xxxxxxxxxxxxxxx" />
                <FormField label="Meta lead form ID" testid={`campaign-${i}-leadform-id`} value={c.meta_lead_form_id} onChange={(v) => setCampaign(i, 'meta_lead_form_id', v)} placeholder="lead form id" />
                <FormField
                  label="Spend (USD)"
                  testid={`campaign-${i}-spend`}
                  type="number"
                  value={c.spend ?? ''}
                  onChange={(v) => setCampaign(i, 'spend', v === '' ? null : (Number.isNaN(parseFloat(v)) ? null : parseFloat(v)))}
                  placeholder="0"
                />
              </div>
            </div>
          ))
        )}
      </div>

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}

      <div className="flex gap-2 pt-1">
        <button
          data-testid="fitter-editor-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
        >{saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save</button>
        <button
          data-testid="fitter-editor-cancel"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text"
        ><X size={12} /> Cancel</button>
      </div>
    </div>
  );
}


function FormField({ label, value, onChange, testid, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ''}
        className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
      />
    </div>
  );
}


// ===========================================================================
// Ad Drafts — list + wizard
// ===========================================================================

function PromoteOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/ad-drafts/insights${refresh ? '?refresh=true' : ''}`);
      setData(res.data);
    } catch {/* swallow */}
    setLoading(false); setRefreshing(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  const camps = data?.campaigns || [];
  if (!camps.length) return null;
  const t = data?.totals || {};

  const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const daysRunning = (m) => {
    if (!m?.date_start) return '—';
    const s = new Date(m.date_start + 'T00:00:00Z'); const e = new Date((m.date_stop || m.date_start) + 'T00:00:00Z');
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  };
  const rows = camps.map((c) => {
    const m = c.metrics || {}; const g = c.geo_delivery || {};
    const leads = m.leads || 0; const spend = m.spend || 0;
    return {
      id: c.draft_id, name: c.fitter_summary?.name || c.headline || c.campaign_id,
      status: c.dashboard_status?.status || 'draft',
      spend, impressions: m.impressions || 0, reach: m.reach || 0,
      ctr: m.ctr || 0, cpc: m.cpc || 0, leads, cpl: leads ? spend / leads : null,
      geoPct: g.in_state_pct ?? null, geoState: g.target_state, geoLeak: !!g.leaking,
      // Window-aware: campaigns with an end date show days remaining (works for
      // scheduled + running); no-window campaigns fall back to days of data.
      days: c.days_remaining ?? daysRunning(m),
      daysLabel: c.days_remaining != null
        ? `${c.days_remaining}d left`
        : (c.end_date ? 'ended' : (daysRunning(m) === '—' ? '—' : `${daysRunning(m)}`)),
      hasData: !!m.has_data,
    };
  });
  const bestCtr = Math.max(...rows.map((r) => r.ctr || 0));
  const withCpl = rows.filter((r) => r.cpl != null);
  const lowCpl = withCpl.length ? Math.min(...withCpl.map((r) => r.cpl)) : null;
  const sorted = [...rows].sort((a, b) => {
    const dir = sort.dir === 'desc' ? -1 : 1;
    let av = a[sort.key], bv = b[sort.key];
    if (sort.key === 'name' || sort.key === 'status') { av = (av || '').toString(); bv = (bv || '').toString(); }
    else { av = av == null ? -1 : av; bv = bv == null ? -1 : bv; }
    return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
  });
  const setKey = (key) => setSort((p) => ({ key, dir: p.key === key && p.dir === 'desc' ? 'asc' : 'desc' }));
  const COLS = [
    ['name', 'Campaign', 'left'], ['status', 'Status', 'left'], ['spend', 'Spend', 'right'],
    ['impressions', 'Impr', 'right'], ['reach', 'Reach', 'right'], ['ctr', 'CTR', 'right'],
    ['cpc', 'CPC', 'right'], ['leads', 'Leads', 'right'], ['cpl', 'CPL', 'right'],
    ['geoPct', 'Geo%', 'right'], ['days', 'Days', 'right'],
  ];

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="promote-overview">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
          <BarChart3 size={12} /> Portfolio overview
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
          {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </div>
      {/* totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Total spend</div>
          <div className="font-display text-xl text-hg-text">{money(t.total_spend)}</div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Total leads</div>
          <div className="font-display text-xl text-hg-text">{t.total_leads ?? 0}</div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Blended CPL</div>
          <div className="font-display text-xl text-hg-green">{money(t.blended_cpl)}</div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Blended CTR</div>
          <div className="font-display text-xl text-hg-text">{t.blended_ctr ?? 0}%</div>
        </div>
      </div>
      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-sans">
          <thead>
            <tr className="text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
              {COLS.map(([key, label, align]) => (
                <th key={key} onClick={() => setKey(key)}
                  className={`px-2 py-2 font-normal cursor-pointer select-none hover:text-hg-text text-${align} ${sort.key === key ? 'text-hg-green' : ''}`}>
                  {label}{sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-b border-hg-border/40 hover:bg-hg-bg/40" data-testid={`overview-row-${r.id}`}>
                <td className="px-2 py-2 text-hg-text max-w-[180px] truncate">{r.name}</td>
                <td className="px-2 py-2">
                  <span className={`text-[9px] uppercase tracking-wider ${r.status === 'running' ? 'text-hg-green' : r.status === 'paused' ? 'text-yellow-400' : r.status === 'rejected' ? 'text-red-400' : 'text-hg-text-dim'}`}>{r.status}</span>
                </td>
                <td className="px-2 py-2 text-right text-hg-text-dim">{money(r.spend)}</td>
                <td className="px-2 py-2 text-right text-hg-text-dim">{r.impressions.toLocaleString()}</td>
                <td className="px-2 py-2 text-right text-hg-text-dim">{r.reach.toLocaleString()}</td>
                <td className={`px-2 py-2 text-right ${r.ctr === bestCtr && r.ctr > 0 ? 'text-hg-green font-semibold' : 'text-hg-text'}`}>{r.ctr ? r.ctr.toFixed(2) + '%' : '—'}</td>
                <td className="px-2 py-2 text-right text-hg-text-dim">{r.cpc ? money(r.cpc) : '—'}</td>
                <td className="px-2 py-2 text-right text-hg-text">{r.leads || '—'}</td>
                <td className={`px-2 py-2 text-right ${r.cpl != null && r.cpl === lowCpl ? 'text-hg-green font-semibold' : 'text-hg-text'}`}>{r.cpl != null ? money(r.cpl) : '—'}</td>
                <td className={`px-2 py-2 text-right ${r.geoPct == null ? 'text-hg-text-dim' : r.geoLeak ? 'text-red-400' : 'text-hg-green'}`}>{r.geoPct == null ? '—' : `${r.geoPct}%`}</td>
                <td className="px-2 py-2 text-right text-hg-text-dim">{r.daysLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-hg-text-dim font-sans">Best CTR &amp; lowest CPL highlighted green · geo % below 50% flagged red · click a column to sort.</div>
    </div>
  );
}

// ===========================================================================
// Phase 3 — cost-per-fitting analytics (funnel + cost, per campaign & fitter)
// ===========================================================================

const _money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const _pct = (v) => (v == null ? '—' : `${Number(v).toFixed(0)}%`);

// A metric cell that is honest about small samples: "—" when there's no denominator,
// and a "†" low-confidence marker (with tooltip) when it's backed by very few conversions.
function MetricCell({ metric, fmt = _money, align = 'right' }) {
  if (!metric || metric.value == null) {
    return <span className="text-hg-text-dim">—</span>;
  }
  return (
    <span className={metric.low_confidence ? 'text-hg-text' : 'text-hg-text'}>
      {fmt(metric.value)}
      {metric.low_confidence && (
        <span
          title={`Based on only ${metric.n} — early, low confidence`}
          className="ml-0.5 text-[9px] align-super text-yellow-400 cursor-help"
        >†{metric.n}</span>
      )}
    </span>
  );
}

// The Leads → Contacted → Booked → Won funnel as decreasing single-hue (brand green)
// magnitude bars, with the step-conversion % between stages. Identity is carried by the
// stage labels, not color, so it stays colorblind-safe.
function FunnelStrip({ funnel, conversion }) {
  const leads = funnel.leads || 0;
  const stages = [
    ['Leads', funnel.leads, null],
    ['Contacted', funnel.contacted, conversion.lead_to_contacted],
    ['Booked', funnel.booked, conversion.contacted_to_booked],
    ['Won', funnel.won, conversion.booked_to_won],
  ];
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="funnel-strip">
      {stages.map(([label, count, conv], i) => {
        const w = leads ? Math.round(((count || 0) / leads) * 100) : 0;
        return (
          <div key={label} className="bg-hg-bg/60 border border-hg-border rounded-sm px-2.5 py-2">
            <div className="flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-wider text-hg-text-dim">{label}</span>
              {conv && conv.value != null && (
                <span className="text-[9px] font-sans text-hg-text-dim" title="conversion from the prior stage">
                  {_pct(conv.value)}{conv.low_confidence ? '†' : ''}
                </span>
              )}
            </div>
            <div className="font-display text-xl text-hg-text leading-tight">{count ?? 0}</div>
            <div className="mt-1 h-1.5 bg-hg-border/40 rounded-full overflow-hidden">
              <div className="h-full bg-hg-green rounded-full" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const StaleBadge = ({ stale }) => {
  if (!stale || !stale.count) return <span className="text-hg-text-dim">—</span>;
  const note = stale.reminded
    ? `${stale.reminded} reminded, still New (up to ${stale.max_reminder_count}×)`
    : 'still New past 2 days';
  return (
    <span
      title={note}
      className="inline-flex items-center gap-1 text-[10px] font-sans text-yellow-400 border border-yellow-400/40 bg-yellow-400/10 rounded-sm px-1.5 py-0.5"
    >
      <AlertTriangle size={10} /> {stale.count}
      {stale.max_reminder_count > 0 && <span className="text-yellow-500/80">·{stale.max_reminder_count}×</span>}
    </span>
  );
};

// Sortable per-campaign / per-fitter table. Read-only (status changes live in the inbox).
function FunnelCostTable({ rows, nameLabel }) {
  const [sort, setSort] = useState({ key: 'per_booked', dir: 'asc' });

  const accessor = (r, key) => {
    switch (key) {
      case 'name': return (r.label || r.fitter_name || r.campaign_id || '').toString().toLowerCase();
      case 'leads': return r.funnel.leads;
      case 'contacted': return r.funnel.contacted;
      case 'booked': return r.funnel.booked;
      case 'won': return r.funnel.won;
      case 'spend': return r.cost.spend;
      case 'per_lead': return r.cost.per_lead.value;
      case 'per_booked': return r.cost.per_booked.value;
      case 'per_won': return r.cost.per_won.value;
      case 'conv': return r.conversion.lead_to_contacted.value;
      case 'stale': return r.stale.count;
      default: return null;
    }
  };
  // Cost sorts ascend by default (cheapest fitting = best); "—"/null always sinks to the end.
  const costKeys = new Set(['per_lead', 'per_booked', 'per_won']);
  const sorted = [...rows].sort((a, b) => {
    const dir = sort.dir === 'desc' ? -1 : 1;
    let av = accessor(a, sort.key), bv = accessor(b, sort.key);
    if (sort.key === 'name') return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    const aNull = av == null, bNull = bv == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;          // nulls always last regardless of dir
    if (bNull) return -1;
    return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
  });
  const setKey = (key) => setSort((p) => ({
    key,
    dir: p.key === key ? (p.dir === 'desc' ? 'asc' : 'desc') : (costKeys.has(key) ? 'asc' : 'desc'),
  }));

  const COLS = [
    ['name', nameLabel, 'left'], ['leads', 'Leads', 'right'], ['contacted', 'Contacted', 'right'],
    ['booked', 'Booked', 'right'], ['won', 'Won', 'right'], ['conv', 'L→C', 'right'],
    ['spend', 'Spend', 'right'], ['per_lead', 'C/Lead', 'right'],
    ['per_booked', 'C/Booked', 'right'], ['per_won', 'C/Won', 'right'], ['stale', 'Stale', 'right'],
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-sans">
        <thead>
          <tr className="text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
            {COLS.map(([key, label, align]) => (
              <th key={key} onClick={() => setKey(key)}
                className={`px-2 py-2 font-normal cursor-pointer select-none hover:text-hg-text text-${align} ${sort.key === key ? 'text-hg-green' : ''}`}>
                {label}{sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.campaign_id || r.fitter_id} className="border-b border-hg-border/40 hover:bg-hg-bg/40"
              data-testid={`analytics-row-${r.campaign_id || r.fitter_id}`}>
              <td className="px-2 py-2 text-hg-text max-w-[190px] truncate" title={r.label || r.fitter_name}>
                {r.label || r.fitter_name || r.campaign_id}
                {r.fitter_name && r.label && r.label !== r.fitter_name && (
                  <span className="text-hg-text-dim"> · {r.fitter_name}</span>
                )}
              </td>
              <td className="px-2 py-2 text-right text-hg-text">{r.funnel.leads || '—'}</td>
              <td className="px-2 py-2 text-right text-hg-text-dim">{r.funnel.contacted || '—'}</td>
              <td className="px-2 py-2 text-right text-hg-text-dim">{r.funnel.booked || '—'}</td>
              <td className="px-2 py-2 text-right text-hg-green">{r.funnel.won || '—'}</td>
              <td className="px-2 py-2 text-right"><MetricCell metric={r.conversion.lead_to_contacted} fmt={_pct} /></td>
              <td className="px-2 py-2 text-right text-hg-text-dim">{_money(r.cost.spend)}</td>
              <td className="px-2 py-2 text-right"><MetricCell metric={r.cost.per_lead} /></td>
              <td className="px-2 py-2 text-right font-semibold"><MetricCell metric={r.cost.per_booked} /></td>
              <td className="px-2 py-2 text-right font-semibold"><MetricCell metric={r.cost.per_won} /></td>
              <td className="px-2 py-2 text-right"><StaleBadge stale={r.stale} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadFunnelAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState('campaign');

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/promote/leads/analytics${refresh ? '?refresh=true' : ''}`);
      setData(res.data);
    } catch {/* swallow */}
    setLoading(false); setRefreshing(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  if (!data) return null;
  const t = data.totals;
  const campaigns = data.campaigns || [];
  const fitters = data.fitters || [];
  const staleH = data.thresholds?.stale_hours ?? 48;
  const anyData = (t.funnel.leads || 0) > 0 || (t.cost.spend || 0) > 0;
  if (!anyData) return null;

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="lead-analytics">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
          <Target size={12} /> Cost per fitting · funnel + spend
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
          {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </div>

      {/* portfolio funnel */}
      <FunnelStrip funnel={t.funnel} conversion={t.conversion} />

      {/* headline cost tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Total spend</div>
          <div className="font-display text-xl text-hg-text">{_money(t.cost.spend)}</div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Cost / booked</div>
          <div className="font-display text-xl text-hg-green"><MetricCell metric={t.cost.per_booked} /></div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Cost / won</div>
          <div className="font-display text-xl text-hg-green"><MetricCell metric={t.cost.per_won} /></div>
        </div>
        <div className="bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-hg-text-dim">Stale (New &gt;{Math.round(staleH / 24)}d)</div>
          <div className="font-display text-xl text-hg-text inline-flex items-center gap-1.5">
            {t.stale.count || 0}
            {t.stale.count > 0 && <AlertTriangle size={14} className="text-yellow-400" />}
          </div>
        </div>
      </div>

      {/* per-campaign / per-fitter toggle + table */}
      <div className="flex items-center gap-1 pt-1">
        {[['campaign', 'By campaign'], ['fitter', 'By fitter']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            data-testid={`analytics-view-${id}`}
            className={`px-2.5 py-1 text-[10px] font-sans rounded-sm border transition-colors ${
              view === id ? 'text-hg-green border-hg-green/50 bg-hg-green/10' : 'text-hg-text-dim border-hg-border hover:text-hg-text'
            }`}>{label}</button>
        ))}
      </div>
      {view === 'campaign'
        ? <FunnelCostTable rows={campaigns} nameLabel="Campaign" />
        : <FunnelCostTable rows={fitters} nameLabel="Fitter" />}

      <div className="text-[10px] text-hg-text-dim font-sans leading-relaxed border-t border-hg-border pt-2">
        Read-only — update statuses from the Leads inbox. <span className="text-yellow-400">†</span> = based on very few
        conversions (early, low confidence — don't over-read it). <span className="text-yellow-400">Stale</span> = leads
        still on “New” past {Math.round(staleH / 24)} days (with reminder count). “—” = no data yet. Cost columns sort
        cheapest-first; click any column to sort.
      </div>
    </div>
  );
}

function AdDraftsView() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openDraft, setOpenDraft] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ad-drafts/list`);
      setDrafts(res.data.drafts || []);
    } catch {/* swallow */}
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  if (creating) {
    return <CreateAdWizard onDone={async (newDraft) => { setCreating(false); await reload(); if (newDraft) setOpenDraft(newDraft); }} onCancel={() => setCreating(false)} />;
  }
  if (openDraft) {
    return <DraftDetail draft={openDraft} onClose={async () => { setOpenDraft(null); await reload(); }} />;
  }

  return (
    <div className="space-y-3">
      <PromoteOverview />
      <LeadFunnelAnalytics />
      <div className="flex justify-end">
        <button
          data-testid="new-draft-btn"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90"
        ><Plus size={12} /> New ad draft</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
      ) : drafts.length === 0 ? (
        <div className="text-center py-12 text-xs text-hg-text-dim font-sans" data-testid="ad-drafts-empty">
          No ad drafts yet. Click "New ad draft" to use AI to generate three options for a fitter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="ad-drafts-list">
          {drafts.map((d) => <DraftRow key={d.id} draft={d} onOpen={() => setOpenDraft(d)} />)}
        </div>
      )}
    </div>
  );
}


function DraftRow({ draft, onOpen }) {
  const fs = draft.fitter_summary || {};
  const badge = STATUS_BADGE[draft.status] || STATUS_BADGE.draft;
  const totalBudget = draft.budget?.total_budget_usd || 0;
  return (
    <button
      onClick={onOpen}
      data-testid={`ad-draft-row-${draft.id}`}
      className="w-full text-left bg-hg-surface border border-hg-border rounded-sm p-3 hover:border-hg-green/40 transition-colors"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${badge.color}`}>{badge.label}</span>
            <span className="text-sm font-sans text-hg-text truncate">{draft.creative?.headline || '(untitled)'}</span>
          </div>
          <div className="text-[11px] font-sans text-hg-text-dim mt-1 truncate">
            {fs.name ? `For ${fs.name}` : ''}{fs.city ? ` — ${fs.city}, ${fs.state || ''}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-sans text-hg-text-dim flex-shrink-0">
          {totalBudget > 0 && <span className="inline-flex items-center gap-1"><DollarSign size={11} /> ${totalBudget.toLocaleString()}</span>}
          <ChevronRight size={12} />
        </div>
      </div>
    </button>
  );
}


function CreateAdWizard({ onDone, onCancel }) {
  const [step, setStep] = useState(1);
  const [fitters, setFitters] = useState([]);
  const [fitter, setFitter] = useState(null);
  const [angle, setAngle] = useState(ANGLE_PRESETS[0]);
  const [extraContext, setExtraContext] = useState('');
  const [objective, setObjective] = useState('lead_gen');
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [creative, setCreative] = useState(null);
  const [budget, setBudget] = useState({ total_budget_usd: 250, hg_contribution_usd: 0, fitter_contribution_usd: 250 });
  const [targetingRadius, setTargetingRadius] = useState(25);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/promoted-fitters/list?only_active=true`);
        setFitters(res.data.fitters || []);
      } catch (e) { setError(e.response?.data?.detail || 'Failed to load fitters.'); }
    })();
  }, []);

  const runGenerate = async () => {
    if (!fitter) return;
    setGenerating(true); setError(''); setDrafts([]); setSelectedIdx(null);
    try {
      const res = await axios.post(`${API}/ad-drafts/generate`, {
        fitter_id: fitter.id,
        angle,
        objective,
        extra_context: extraContext || null,
      });
      setDrafts(res.data.drafts || []);
      setTargetingRadius(fitter.default_radius_miles || 25);
      setBudget({
        total_budget_usd: fitter.default_monthly_budget_usd || 250,
        hg_contribution_usd: 0,
        fitter_contribution_usd: fitter.default_monthly_budget_usd || 250,
      });
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'AI generation failed. Please try again.');
    }
    setGenerating(false);
  };

  const handleSave = async () => {
    if (selectedIdx == null) { setError('Pick a draft to save.'); return; }
    setSaving(true); setError('');
    try {
      const creativePayload = creative || drafts[selectedIdx];
      const payload = {
        fitter_id: fitter.id,
        objective,
        creative: {
          headline: creativePayload.headline,
          primary_text: creativePayload.primary_text,
          description: creativePayload.description || '',
          cta_text: creativePayload.cta_text || 'Learn More',
          image_data: creativePayload.image_data || '',
          video_url: (creativePayload.video_url || '').trim(),
          thumbnail_url: (creativePayload.thumbnail_url || '').trim(),
          includes_fitter_photo: !!fitter.consent_to_use_photo,
        },
        targeting: {
          radius_miles: targetingRadius,
          age_min: 30,
          age_max: 75,
          interests: ['Golf', 'Golf courses', 'PGA Tour', 'Golf equipment'],
          locales: ['en_US'],
        },
        budget,
        notes: extraContext || null,
      };
      const res = await axios.post(`${API}/ad-drafts/save`, payload);
      await onDone(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div className="bg-hg-surface border border-hg-green/30 rounded-sm p-4 space-y-4" data-testid="create-ad-wizard">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-sans text-hg-text uppercase tracking-wider">New ad draft — step {step} of 3</h3>
        <button onClick={onCancel} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
      </div>

      {step === 1 && (
        <div className="space-y-3" data-testid="wizard-step-1">
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Promote which fitter?</label>
            <select
              data-testid="wizard-fitter-select"
              value={fitter?.id || ''}
              onChange={(e) => setFitter(fitters.find((f) => f.id === e.target.value))}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            >
              <option value="">-- pick a fitter --</option>
              {fitters.map((f) => <option key={f.id} value={f.id}>{f.name} — {f.facility_name || f.city || ''}</option>)}
            </select>
            {fitters.length === 0 && <p className="text-[11px] font-sans text-yellow-400 mt-1">No active fitters yet. Add one in the Fitter Directory tab first.</p>}
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Campaign objective</label>
            <select data-testid="wizard-objective" value={objective} onChange={(e) => setObjective(e.target.value)} className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green">
              {OBJECTIVE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Creative angle</label>
            <select data-testid="wizard-angle" value={angle} onChange={(e) => setAngle(e.target.value)} className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green">
              {ANGLE_PRESETS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Anything else? (optional)</label>
            <textarea
              data-testid="wizard-extra-context"
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              rows={2}
              placeholder="e.g., 'Highlight that they specialize in junior fittings' or 'Tied to a fall fitting event'"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
            />
          </div>
          {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
          <div className="flex gap-2">
            <button
              data-testid="wizard-generate-btn"
              onClick={runGenerate}
              disabled={!fitter || generating}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            >{generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Generate 3 drafts</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-step-2">
          <p className="text-xs text-hg-text-dim font-sans">Three drafts generated. Pick one, edit if needed.</p>
          <div className="space-y-2">
            {drafts.map((d, i) => (
              <button
                key={i}
                data-testid={`wizard-draft-${i}`}
                onClick={() => { setSelectedIdx(i); setCreative(d); }}
                className={`w-full text-left bg-hg-bg border rounded-sm p-3 transition-colors ${selectedIdx === i ? 'border-hg-green' : 'border-hg-border hover:border-hg-green/40'}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                  <span className="text-[10px] font-sans uppercase tracking-wider text-hg-green">{d.angle}</span>
                  {d.lint && (d.lint.clean
                    ? <span className="text-[10px] font-sans text-hg-green inline-flex items-center gap-0.5"><CheckCircle2 size={9} /> clean</span>
                    : <span className="text-[10px] font-sans text-yellow-400 inline-flex items-center gap-0.5"><AlertTriangle size={9} /> {d.lint.error_count} errors</span>)
                  }
                </div>
                <div className="font-sans text-sm text-hg-text font-medium">{d.headline} <span className="text-hg-text-dim text-[10px] ml-1">({d.headline?.length || 0}c)</span></div>
                <div className="text-xs text-hg-text font-sans mt-1 leading-relaxed">{d.primary_text}</div>
                {d.description && <div className="text-[11px] text-hg-text-dim font-sans mt-1">{d.description}</div>}
                <div className="text-[10px] text-hg-text-dim font-sans mt-1.5">CTA: {d.cta_text}</div>
                {d.note && <div className="text-[10px] text-hg-text-dim font-sans italic mt-1">{d.note}</div>}
              </button>
            ))}
          </div>
          {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
          <div className="flex gap-2">
            <button data-testid="wizard-back-btn" onClick={() => setStep(1)} className="px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">Back</button>
            <button
              data-testid="wizard-next-btn"
              disabled={selectedIdx == null}
              onClick={() => setStep(3)}
              className="px-4 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            >Next — targeting & budget</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="wizard-step-3">
          {creative && (
            <div className="bg-hg-bg border border-hg-border rounded-sm p-3 space-y-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Selected creative — final edits</div>
              <FormField label="Headline (max 40)" testid="wizard-edit-headline" value={creative.headline} onChange={(v) => setCreative({ ...creative, headline: v })} />
              <div>
                <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Primary text</label>
                <textarea
                  data-testid="wizard-edit-primary"
                  value={creative.primary_text}
                  onChange={(e) => setCreative({ ...creative, primary_text: e.target.value })}
                  rows={3}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                />
              </div>
              <FormField label="Description (max 30)" testid="wizard-edit-desc" value={creative.description} onChange={(v) => setCreative({ ...creative, description: v })} />
            </div>
          )}

          {/* Creative type: image or video */}
          <div className="flex items-center gap-2" data-testid="wizard-creative-type">
            <span className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">Creative</span>
            {['image', 'video'].map((t) => {
              const active = (creative?.video_url ? 'video' : 'image') === t;
              return (
                <button
                  key={t}
                  data-testid={`wizard-creative-${t}`}
                  onClick={() => setCreative({ ...creative, video_url: t === 'video' ? (creative?.video_url || ' ') : '', ...(t === 'image' ? { thumbnail_url: '' } : {}) })}
                  className={`px-3 py-1 text-xs font-sans rounded-sm border transition-colors ${active ? 'bg-hg-green text-hg-text border-hg-green' : 'border-hg-border text-hg-text-dim hover:text-hg-text'}`}
                >{t === 'image' ? 'Image' : 'Video'}</button>
              );
            })}
          </div>

          {creative?.video_url ? (
            <div className="space-y-2 border border-hg-border rounded-sm p-3 bg-hg-bg/40" data-testid="wizard-video-panel">
              <FormField label="Video URL (Cloudinary hg-library .mp4)" testid="wizard-video-url"
                value={(creative.video_url || '').trim()} onChange={(v) => setCreative({ ...creative, video_url: v })} />
              <FormField label="Thumbnail URL (optional — auto-generated from a Cloudinary video)" testid="wizard-thumb-url"
                value={creative.thumbnail_url || ''} onChange={(v) => setCreative({ ...creative, thumbnail_url: v })} />
              <div className="text-[11px] font-sans text-hg-text-dim leading-relaxed">
                Meta uploads &amp; processes the video before the ad builds (a few seconds–minutes). If it's still
                processing when you publish, you'll get a "video still processing — publish again shortly" message; the
                upload is saved, so just retry. A thumbnail is required — leave blank to auto-use a frame from a
                Cloudinary-hosted video.
              </div>
            </div>
          ) : (
            /* Image attachment */
            <EmailImagePanel
              testIdPrefix="wizard-image"
              imageData={creative?.image_data || null}
              onImageSet={(data) => setCreative({ ...creative, image_data: data || '' })}
              context="hero"
              promptContext={`paid ad for HG-certified teacher/fitter ${fitter?.name || ''} at ${fitter?.facility_name || fitter?.city || 'their facility'} — golfer lifestyle, no club close-ups, real-looking editorial`}
              label="Ad image (1080×1080 ideal for Meta Lead Ads — AI images auto-watermarked; club images are blocked)"
              size="compact"
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormField label="Radius (miles)" testid="wizard-radius" type="number" value={targetingRadius} onChange={(v) => setTargetingRadius(parseInt(v, 10) || 25)} />
            <FormField label="HG contribution (USD)" testid="wizard-hg-contribution" type="number" value={budget.hg_contribution_usd} onChange={(v) => setBudget({ ...budget, hg_contribution_usd: parseFloat(v) || 0, total_budget_usd: (parseFloat(v) || 0) + (budget.fitter_contribution_usd || 0) })} />
            <FormField label="Fitter contribution (USD)" testid="wizard-fitter-contribution" type="number" value={budget.fitter_contribution_usd} onChange={(v) => setBudget({ ...budget, fitter_contribution_usd: parseFloat(v) || 0, total_budget_usd: (budget.hg_contribution_usd || 0) + (parseFloat(v) || 0) })} />
          </div>
          <div className="text-[11px] font-sans text-hg-text-dim">Total: ${(budget.total_budget_usd || 0).toLocaleString()}</div>

          {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
          <div className="flex gap-2">
            <button data-testid="wizard-back2-btn" onClick={() => setStep(2)} className="px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">Back</button>
            <button
              data-testid="wizard-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            >{saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ===========================================================================
// Draft detail / approve view
// ===========================================================================

// Derive a still-frame poster from a Cloudinary VIDEO url (mirrors the backend).
function cloudinaryPoster(url) {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/video/upload/')) return null;
  const [base, after] = url.split('/video/upload/');
  return `${base}/video/upload/so_2,f_jpg,q_auto/${after.replace(/\.[a-zA-Z0-9]+$/, '')}.jpg`;
}

function DraftDetail({ draft, onClose }) {
  const [current, setCurrent] = useState(draft);
  const [hydrated, setHydrated] = useState(null);  // fitter pulled from server
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [pubStatus, setPubStatus] = useState(null);  // read-back of Meta objects
  const [pubMsg, setPubMsg] = useState('');
  const [insights, setInsights] = useState(null);    // Phase D performance
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Memoised on draft.id so the hydrate effect below can depend on them honestly.
  // Left as plain functions they were rebuilt every render, so listing them as
  // dependencies would re-fire the effect on every render it caused — a fetch loop.
  // Keyed to draft.id, they change only when the open draft does.
  const refreshStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ad-drafts/${draft.id}/publish-status`);
      setPubStatus(res.data);
    } catch {/* swallow */}
  }, [draft.id]);

  const loadInsights = useCallback(async (force = false) => {
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/ad-drafts/${draft.id}/insights`, { params: { refresh: force } });
      setInsights(res.data);
    } catch {/* swallow — panel shows its own empty state */}
    setInsightsLoading(false);
  }, [draft.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ad-drafts/${draft.id}`);
        setCurrent(res.data);
        setHydrated(res.data.fitter);
        if (res.data?.publish?.meta_campaign_id) { refreshStatus(); loadInsights(false); }
      } catch (e) { setError(e.response?.data?.detail || 'Failed to load draft.'); }
    })();
  }, [draft.id, refreshStatus, loadInsights]);

  // Phase A: publish the full lead-ad graph to Meta, all PAUSED ($0 spend).
  const publishPaused = async () => {
    if (!window.confirm(
      'Publish this campaign to Meta in PAUSED state?\n\n'
      + 'Everything is created paused for review in Ads Manager. No budget is spent, '
      + 'and nothing goes live — activation is a separate Phase B step.')) return;
    setPublishing(true); setError(''); setPubMsg('');
    try {
      const res = await axios.post(`${API}/ad-drafts/${draft.id}/publish?mode=paused&with_form=true`);
      setCurrent((cur) => ({ ...cur, status: 'paused', publish: res.data.publish }));
      setPubMsg('Published to Meta — all objects PAUSED, $0 spend.');
      await refreshStatus();
    } catch (e) { setError(e.response?.data?.detail || 'Publish failed.'); }
    setPublishing(false);
  };

  // Phase B — activate (money path), two-step spend confirm + kill switch.
  const [activateStep, setActivateStep] = useState('idle'); // idle | confirming
  const [preview, setPreview] = useState(null);
  const [acting, setActing] = useState(false);
  // Recruitment campaigns must run WINDOWED — the server refuses an uncapped launch.
  // Default 5-day window; the field feeds run_days on both the dry-run and the confirm.
  const isRecruit = ((current.fitter_id || draft.fitter_id) || '').startsWith('pf-recruit-');
  const [runDays, setRunDays] = useState(5);
  const dailyCap = current.budget?.daily_cap_usd ?? draft.budget?.daily_cap_usd ?? null;
  const runDaysParam = isRecruit ? `&run_days=${Math.max(1, Math.min(60, Number(runDays) || 5))}` : '';

  const startActivate = async () => {
    setActing(true); setError('');
    try {
      // dry-run: runs all guardrails + builds the production form/creative, does NOT go live
      const res = await axios.post(`${API}/ad-drafts/${draft.id}/activate?confirm=true&dry_run=true${runDaysParam}`);
      setPreview(res.data);
      setActivateStep('confirming');
    } catch (e) { setError(e.response?.data?.detail || 'Could not prepare activation.'); }
    setActing(false);
  };
  const confirmActivate = async () => {
    setActing(true); setError('');
    try {
      const res = await axios.post(`${API}/ad-drafts/${draft.id}/activate?confirm=true${runDaysParam}`);
      setCurrent((c) => ({ ...c, status: 'running', publish: res.data.publish }));
      setActivateStep('idle'); setPreview(null); setPubMsg('Campaign is LIVE.');
      await refreshStatus();
    } catch (e) { setError(e.response?.data?.detail || 'Activation failed.'); }
    setActing(false);
  };
  const cancelActivate = () => { setActivateStep('idle'); setPreview(null); };

  const lifecycle = async (action, confirm = false) => {
    if (action === 'stop' && !window.confirm('Stop this campaign? It will be paused and marked completed.')) return;
    setActing(true); setError('');
    try {
      const res = await axios.post(`${API}/ad-drafts/${draft.id}/${action}${confirm ? '?confirm=true' : ''}`);
      const nextStatus = res.data.status === 'running' ? 'running' : (action === 'stop' ? 'completed' : 'paused');
      setCurrent((c) => ({ ...c, status: nextStatus }));
      await refreshStatus();
    } catch (e) { setError(e.response?.data?.detail || `${action} failed.`); }
    setActing(false);
  };

  const approve = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/ad-drafts/${draft.id}/approve`);
      setCurrent(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Approve failed.'); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this draft?')) return;
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/ad-drafts/${draft.id}`);
      await onClose();
    } catch (e) { setError(e.response?.data?.detail || 'Delete failed.'); }
    setBusy(false);
  };

  const updatePrimary = async (newPrimary) => {
    setCurrent({ ...current, creative: { ...current.creative, primary_text: newPrimary } });
    try {
      await axios.patch(`${API}/ad-drafts/${draft.id}`, {
        creative: { ...current.creative, primary_text: newPrimary },
      });
    } catch {/* swallow — user can retry */}
  };

  const badge = STATUS_BADGE[current.status] || STATUS_BADGE.draft;
  const c = current.creative || {};
  const t = current.targeting || {};
  const b = current.budget || {};
  const fitter = hydrated || {};
  const isVideo = !!(c.video_url || '').trim();
  const videoThumb = (c.thumbnail_url || '').trim()
    || (isVideo ? cloudinaryPoster(c.video_url) : null)
    || current.publish?.meta_video_thumb || null;

  return (
    <div data-testid="draft-detail" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onClose} className="text-xs font-sans text-hg-text-dim hover:text-hg-text inline-flex items-center gap-1"><X size={11} /> Back to drafts</button>
        <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border ${badge.color}`}>{badge.label}</span>
      </div>

      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Promoted fitter</div>
            <div className="text-sm font-sans text-hg-text font-medium mt-0.5">{fitter.name || '—'}</div>
            {fitter.facility_name && <div className="text-[11px] font-sans text-hg-text-dim">{fitter.facility_name}{fitter.city ? ` — ${fitter.city}, ${fitter.state || ''}` : ''}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Objective</div>
            <div className="text-sm font-sans text-hg-text mt-0.5 capitalize">{current.objective?.replace('_', ' ')}</div>
          </div>
        </div>
      </div>

      {/* Creative preview */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="draft-creative-preview">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Ad preview</div>
        <div className="bg-white text-black p-4 rounded-sm space-y-2 max-w-md">
          <div className="text-[11px] font-medium text-gray-500">Sponsored · Henry-Griffitts</div>
          <div className="text-sm leading-snug">{c.primary_text}</div>
          {isVideo ? (
            <video
              data-testid="draft-video-preview"
              src={(c.video_url || '').trim()}
              poster={videoThumb || undefined}
              controls
              playsInline
              className="w-full aspect-square object-cover rounded-sm border border-gray-200 bg-black"
            />
          ) : c.image_data ? (
            <img
              data-testid="draft-image-preview"
              src={c.image_data}
              alt="ad creative"
              className="w-full aspect-square object-cover rounded-sm border border-gray-200"
            />
          ) : null}
          <div className="mt-2 bg-gray-100 rounded-sm p-3 border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">henry-griffitts.com</div>
            <div className="text-sm font-medium text-gray-900 mt-0.5">{c.headline}</div>
            {c.description && <div className="text-xs text-gray-600">{c.description}</div>}
            <button className="mt-2 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-sm">{c.cta_text}</button>
          </div>
        </div>
      </div>

      {/* Creative management (post-save) — video shows a player, image shows the uploader */}
      {isVideo ? (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2" data-testid="draft-video-panel">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Video creative</div>
          <video src={(c.video_url || '').trim()} poster={videoThumb || undefined} controls playsInline
            className="w-full max-w-sm aspect-square object-cover rounded-sm border border-hg-border bg-black" />
          <div className="text-[11px] font-sans text-hg-text-dim break-all">
            <span className="text-hg-text-dim/70">Video: </span>
            <a href={(c.video_url || '').trim()} target="_blank" rel="noreferrer" className="text-hg-green hover:underline">{(c.video_url || '').trim()}</a>
          </div>
          {videoThumb && (
            <div className="text-[11px] font-sans text-hg-text-dim break-all">
              <span className="text-hg-text-dim/70">Thumbnail (poster frame): </span>
              <a href={videoThumb} target="_blank" rel="noreferrer" className="text-hg-green hover:underline">{videoThumb}</a>
            </div>
          )}
          <div className="text-[11px] font-sans text-hg-text-dim">Video ad — the image uploader is hidden for this draft. Edit the video/thumbnail via the create wizard.</div>
        </div>
      ) : (
        <EmailImagePanel
          testIdPrefix={`draft-${draft.id}-image`}
          imageData={c.image_data || null}
          onImageSet={async (newData) => {
            const newCreative = { ...c, image_data: newData || '' };
            setCurrent({ ...current, creative: newCreative });
            try {
              await axios.patch(`${API}/ad-drafts/${draft.id}`, { creative: newCreative });
            } catch { /* user can retry */ }
          }}
          context="hero"
          promptContext={`paid ad for HG-certified teacher/fitter ${fitter.name || ''} at ${fitter.facility_name || fitter.city || 'their facility'} — golfer lifestyle, no club close-ups, real-looking editorial`}
          label="Ad image (auto-watermarked; club images blocked)"
          size="compact"
        />
      )}

      <ClaimAudit
        text={c.primary_text || ''}
        onTextChange={updatePrimary}
        testid={`draft-${draft.id}-audit`}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DetailCard icon={Globe2} label="Radius" value={`${t.radius_miles || 25} mi`} sub={t.custom_radius_center_address || ''} />
        <DetailCard icon={Users} label="Age range" value={`${t.age_min || 30}–${t.age_max || 75}`} sub={(t.interests || []).slice(0, 3).join(', ')} />
        <DetailCard icon={DollarSign} label="Total budget" value={`$${(b.total_budget_usd || 0).toLocaleString()}`} sub={`HG $${(b.hg_contribution_usd || 0)} · Fitter $${(b.fitter_contribution_usd || 0)}`} />
      </div>

      {/* Phase A — publish to Meta (everything PAUSED, $0 spend) */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="draft-publish-panel">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Publish to Meta</div>
          {current.publish?.meta_campaign_id && (
            <button data-testid="draft-refresh-status" onClick={refreshStatus} className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green"><RefreshCw size={11} /> Refresh status</button>
          )}
        </div>

        {!current.publish?.meta_campaign_id ? (
          current.status === 'approved' ? (
            <div className="space-y-2">
              <p className="text-[11px] font-sans text-hg-text-dim">Creates the full lead-ad campaign on Meta with everything <b className="text-yellow-300">PAUSED</b> — $0 spend, nothing live. Review it in Ads Manager; activation is a separate Phase B step.</p>
              <button
                data-testid="draft-publish-btn"
                onClick={publishPaused}
                disabled={publishing}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
              >{publishing ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Publish to Meta (paused)</button>
            </div>
          ) : (
            <p className="text-[11px] font-sans text-hg-text-dim">Approve this draft to enable publishing.</p>
          )
        ) : (
          <div className="space-y-2" data-testid="draft-published">
            {pubMsg && <div className="text-[11px] font-sans text-hg-green inline-flex items-center gap-1"><CheckCircle2 size={11} /> {pubMsg}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="draft-publish-objects">
              {[
                ['campaign', 'Campaign', current.publish.meta_campaign_id],
                ['ad_set', 'Ad set', current.publish.meta_ad_set_id],
                ['ad', 'Ad', current.publish.meta_ad_id],
              ].map(([key, label, id]) => {
                const o = pubStatus?.objects?.[key];
                return (
                  <div key={key} className="bg-hg-bg border border-hg-border rounded-sm p-2.5">
                    <div className="text-[9px] uppercase tracking-wider text-hg-text-dim font-sans">{label}</div>
                    <div className="text-[11px] font-sans text-hg-text truncate" title={id}>{id || '—'}</div>
                    {o && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-sans text-yellow-300">
                        <PauseCircle size={10} /> {o.status}{o.effective_status && o.effective_status !== o.status ? ` (${o.effective_status})` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] font-sans text-hg-text-dim">
              Lead form: <span className="text-hg-text">{current.publish.meta_lead_form_id || '—'}</span>
              {current.publish.lead_form_placeholder && <span className="text-yellow-400"> (Phase A placeholder — not webhook-wired; replaced by the production form in Phase B)</span>}
            </div>
            <button
              data-testid="draft-resync-btn"
              onClick={publishPaused}
              disabled={publishing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40"
            >{publishing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Re-sync to Meta (idempotent)</button>
            <div className="text-[10px] font-sans text-hg-text-dim italic">Everything is PAUSED — $0 spend until you activate below.</div>
          </div>
        )}
      </div>

      {/* Phase D — performance dashboard (read-only) */}
      {current.publish?.meta_campaign_id && (
        <PerfDashboard
          insights={insights}
          loading={insightsLoading}
          onRefresh={() => loadInsights(true)}
        />
      )}

      {/* Phase B — activate (money path) + kill switch */}
      {current.publish?.meta_campaign_id && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="draft-activate-panel">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Campaign — go live</div>
            {current.status === 'running'
              ? <span className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-green"><span className="w-1.5 h-1.5 rounded-full bg-hg-green animate-pulse" /> LIVE</span>
              : <span className="inline-flex items-center gap-1 text-[10px] font-sans text-yellow-300"><PauseCircle size={11} /> Paused — $0 spend</span>}
          </div>

          {current.status === 'running' ? (
            <div className="space-y-2">
              <p className="text-[11px] font-sans text-hg-text-dim">This campaign is delivering and spending. Use the kill switch any time.</p>
              <div className="flex gap-2">
                <button data-testid="draft-pause-btn" onClick={() => lifecycle('pause')} disabled={acting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-yellow-400/40 text-yellow-300 rounded-sm hover:bg-yellow-400/10 disabled:opacity-40"><PauseCircle size={12} /> Pause</button>
                <button data-testid="draft-stop-btn" onClick={() => lifecycle('stop')} disabled={acting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-500/40 text-red-400 rounded-sm hover:bg-red-500/10 disabled:opacity-40"><X size={12} /> Stop</button>
              </div>
            </div>
          ) : activateStep === 'confirming' && preview ? (
            <div className="space-y-2 border border-yellow-400/40 bg-yellow-400/5 rounded-sm p-3" data-testid="activate-confirm">
              <div className="text-xs font-sans text-yellow-200 font-medium inline-flex items-center gap-1.5"><AlertTriangle size={13} /> Confirm real spend</div>
              <div className="text-[12px] font-sans text-hg-text space-y-0.5">
                <div>Daily budget: <b>${(preview.daily_usd ?? 0).toFixed(2)}</b> / day <span className="text-hg-text-dim">(hard cap $50)</span></div>
                <div>Schedule: <b>{preview.scheduled_days
                  ? `${preview.scheduled_days} days · ends ${new Date(Date.now() + preview.scheduled_days * 86400000).toISOString().slice(0, 10)}`
                  : 'open-ended (no end date)'}</b></div>
                <div>Estimated max spend: <b className="text-yellow-300">{preview.max_spend != null ? `~$${preview.max_spend.toFixed(2)}` : 'ongoing daily spend (set an end date to cap it)'}</b></div>
              </div>
              {(preview.warnings || []).map((w, i) => (
                <div key={i} className="text-[11px] font-sans text-red-300 flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 flex-shrink-0" /> {w}</div>
              ))}
              <div className="flex gap-2 pt-1">
                <button data-testid="activate-confirm-btn" onClick={confirmActivate} disabled={acting} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-red-500 text-white rounded-sm hover:opacity-90 disabled:opacity-40">{acting ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Confirm &amp; go live</button>
                <button data-testid="activate-cancel-btn" onClick={cancelActivate} disabled={acting} className="px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[11px] font-sans text-hg-text-dim">Activation is the only step that spends real money. You'll see the max spend and confirm before anything goes live. Hard cap: <b className="text-hg-text">$50/day</b>, server-enforced.</p>
              {isRecruit && (
                <div className="flex items-center gap-2 text-[11px] font-sans text-hg-text" data-testid="activate-run-days">
                  <span className="text-hg-text-dim">Run for</span>
                  <input
                    type="number" min="1" max="60" value={runDays}
                    onChange={(e) => setRunDays(e.target.value)}
                    className="w-14 bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-xs text-hg-text"
                  />
                  <span className="text-hg-text-dim">days
                    {dailyCap != null && Number(runDays) > 0
                      ? ` · ends ${new Date(Date.now() + Number(runDays) * 86400000).toISOString().slice(0, 10)} · ~$${(dailyCap * Number(runDays)).toFixed(0)} max`
                      : ''}
                  </span>
                </div>
              )}
              <button data-testid="draft-activate-btn" onClick={startActivate} disabled={acting || (isRecruit && !(Number(runDays) >= 1))} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">{acting ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Activate (go live)…</button>
              {current.status === 'completed' && <span className="text-[10px] font-sans text-hg-text-dim ml-2">stopped — can re-activate</span>}
            </div>
          )}
        </div>
      )}

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}

      <div className="flex gap-2 flex-wrap">
        {current.status === 'draft' && (
          <button
            data-testid="draft-approve-btn"
            onClick={approve}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
          >{busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve draft</button>
        )}
        <button
          data-testid="draft-delete-btn"
          onClick={remove}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans border border-hg-border text-red-400 rounded-sm hover:border-red-400 disabled:opacity-40"
        ><Trash2 size={12} /> Delete</button>
      </div>

      <div className="text-[10px] font-sans text-hg-text-dim italic border-t border-hg-border pt-3">
        Approved drafts publish to Meta as a full lead-ad campaign, all PAUSED ($0 spend), for review in Ads Manager. Activation (going live) and the live performance dashboard are built in; lead capture/routing to the fitter is Phase C — coming soon.
      </div>
    </div>
  );
}


// ===========================================================================
// Phase C — Hub Leads inbox
// ===========================================================================

const FOLLOWUP = {
  new:       { label: 'New',       color: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  contacted: { label: 'Contacted', color: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  booked:    { label: 'Booked',    color: 'text-purple-300 border-purple-400/40 bg-purple-400/10' },
  won:       { label: 'Won',       color: 'text-hg-green border-hg-green/50 bg-hg-green/15' },
  lost:      { label: 'Lost',      color: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
};
const FOLLOWUP_ORDER = ['new', 'contacted', 'booked', 'won', 'lost'];

// Read-only email-engagement indicator. Renders only when a lead has synced engagement
// (today that's recruitment leads — the only ones whose sends record email_touches).
function EngagementBadge({ eng, onRefresh, refreshing, bookingFollowupAt }) {
  if (!eng) return null;
  const chip = 'inline-flex items-center gap-1 text-[10px] font-sans rounded-sm px-1.5 py-0.5 border';
  return (
    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid="lead-engagement">
      {!eng.opened ? (
        <span className={`${chip} text-hg-text-dim border-hg-border bg-hg-surface/40`} title="No open recorded yet">
          <Eye size={10} /> Not opened yet
        </span>
      ) : (
        <span className={`${chip} text-blue-300 border-blue-400/40 bg-blue-400/10`} title={`${eng.open_count} open(s) recorded`}>
          <Eye size={10} /> Opened{eng.open_count > 1 ? ` ×${eng.open_count}` : ''}
        </span>
      )}
      {eng.clicked_booking && (
        <span className={`${chip} text-hg-green border-hg-green/50 bg-hg-green/15`} title="Clicked the Book-a-call link">
          <MousePointerClick size={10} /> Clicked booking
        </span>
      )}
      {bookingFollowupAt && (
        <span className={`${chip} text-amber-300 border-amber-400/50 bg-amber-400/10`}
              title="They clicked the booking link days ago but never booked or filled the intake — worth a personal call."
              data-testid="lead-booking-followup">
          <MousePointerClick size={10} /> Clicked to book — never booked · call them
        </span>
      )}
      {eng.clicked_intake && (
        <span className={`${chip} text-purple-300 border-purple-400/40 bg-purple-400/10`} title="Opened the intake form link">
          <FileText size={10} /> Clicked form
        </span>
      )}
      {eng.opened && !eng.clicked && (
        <span className="text-[10px] font-sans text-hg-text-dim">no clicks</span>
      )}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        title="Re-pull engagement from MailerLite (read-only)"
        className="inline-flex items-center text-hg-text-dim hover:text-hg-green disabled:opacity-40"
      >
        {refreshing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
      </button>
      {eng.last_synced_at && (
        <span className="text-[9px] font-sans text-hg-text-dim/70">
          synced {new Date(eng.last_synced_at).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}

// Recruitment intake fields (label + display order) for the read-only lead panel.
const INTAKE_FIELDS = [
  ['facility_name', 'Facility'],
  ['location', 'Location'],
  ['facility_type', 'Type'],
  ['role', 'Role'],
  ['audience_size', 'Students/members'],
  ['offers_fitting', 'Offers fitting'],
  ['why_hg', 'Why Henry-Griffitts'],
  ['anything_else', 'Anything else / reach'],
];

// Retired from this page (2026-07-27) — Leads Tracker is the single home for leads.
// Kept (archive-not-delete) in case a per-campaign lead view returns here later.
// eslint-disable-next-line no-unused-vars
function LeadsInbox() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [engBusy, setEngBusy] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/promote/leads`);
      setLeads(res.data.leads || []);
      setCounts(res.data.counts || {});
    } catch {/* swallow */}
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const resend = async (lead) => {
    if (!window.confirm(`Re-send this lead to ${lead.fitter_name || 'the fitter'}'s routing addresses?`)) return;
    setBusyId(lead.id);
    try {
      const res = await axios.post(`${API}/promote/leads/${lead.id}/resend`);
      flash(res.data?.routing?.status === 'routed' ? `Re-sent to ${(res.data.routing.recipients || []).join(', ')}` : `Resend: ${res.data?.routing?.status}`);
      await reload();
    } catch (e) { flash(e.response?.data?.detail || 'Resend failed'); }
    setBusyId('');
  };

  const setStatus = async (lead, status) => {
    setBusyId(lead.id);
    try {
      await axios.patch(`${API}/promote/leads/${lead.id}`, { follow_up_status: status });
      await reload();
    } catch (e) { flash(e.response?.data?.detail || 'Update failed'); }
    setBusyId('');
  };

  // Read-only re-pull of one lead's open/click data from MailerLite.
  const refreshEngagement = async (id) => {
    setEngBusy(id);
    try {
      const res = await axios.post(`${API}/promote/leads/${id}/engagement/refresh`);
      if (res.data?.engagement) {
        setLeads(prev => prev.map(p => p.id === id ? { ...p, engagement: res.data.engagement } : p));
        flash('Engagement refreshed');
      } else {
        flash(res.data?.message || 'No engagement data yet');
      }
    } catch (e) { flash(e.response?.data?.detail || 'Refresh failed'); }
    setEngBusy('');
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;

  return (
    <div className="space-y-3" data-testid="leads-inbox">
      {msg && <div className="text-[11px] font-sans text-hg-green inline-flex items-center gap-1"><CheckCircle2 size={12} /> {msg}</div>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 text-[11px] font-sans text-hg-text-dim">
          <span className="inline-flex items-center gap-1"><Inbox size={13} /> {counts.total || 0} lead{(counts.total || 0) === 1 ? '' : 's'}</span>
          {!!counts.new && <span className="text-hg-green">{counts.new} new</span>}
          <span>{counts.routed || 0} routed</span>
        </div>
        <button onClick={reload} className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green"><RefreshCw size={11} /> Refresh</button>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-12 text-xs text-hg-text-dim font-sans" data-testid="leads-empty">
          No leads yet. Leads from your live lead-ads land here automatically and are emailed to the fitter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {leads.map((l) => {
            const c = l.contact || {};
            const r = l.routing || {};
            const fu = FOLLOWUP[l.follow_up_status] || FOLLOWUP.new;
            const routed = r.status === 'routed';
            return (
              <div key={l.id} className="bg-hg-surface border border-hg-border rounded-sm p-3" data-testid={`lead-row-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${fu.color}`}>{fu.label}</span>
                      <span className="text-sm font-sans text-hg-text">{c.full_name || '(no name)'}</span>
                      {l.is_test && <span className="text-[9px] font-sans uppercase tracking-wider text-yellow-400 border border-yellow-400/40 bg-yellow-400/10 rounded-sm px-1 py-0.5">Test</span>}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-sans text-hg-text-dim mt-1 flex-wrap">
                      {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-hg-green"><Mail size={11} /> {c.email}</a>}
                      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-hg-green"><Phone size={11} /> {c.phone}</a>}
                      {c.zip && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {c.zip}</span>}
                    </div>
                    {(c.current_role || c.years_in_golf) && (
                      <div className="flex items-center gap-2 text-[11px] font-sans text-hg-text-dim mt-1 flex-wrap" data-testid={`lead-qual-${l.id}`}>
                        {c.current_role && <span className="inline-flex items-center gap-1 bg-hg-bg/60 border border-hg-border rounded-sm px-1.5 py-0.5">Role: <span className="text-hg-text">{c.current_role}</span></span>}
                        {c.years_in_golf && <span className="inline-flex items-center gap-1 bg-hg-bg/60 border border-hg-border rounded-sm px-1.5 py-0.5">Years in golf: <span className="text-hg-text">{c.years_in_golf}</span></span>}
                      </div>
                    )}
                    <div className="text-[10px] font-sans text-hg-text-dim mt-1">
                      For <span className="text-hg-text">{l.fitter_name || '—'}</span>
                      {' · '}
                      {routed
                        ? <span className="text-hg-green">routed to {(r.recipients || []).join(', ')}</span>
                        : <span className="text-yellow-400">{r.status || 'pending'}</span>}
                      {!!(r.suppressed || []).length && <span className="text-red-400"> · suppressed: {r.suppressed.join(', ')}</span>}
                    </div>
                    <EngagementBadge
                      eng={l.engagement}
                      refreshing={engBusy === l.id}
                      onRefresh={() => refreshEngagement(l.id)}
                      bookingFollowupAt={l.booking_followup_flagged_at}
                    />
                    {l.intake && (
                      <div className="mt-2 bg-hg-bg/50 border border-hg-border rounded-sm p-2" data-testid={`lead-intake-${l.id}`}>
                        <div className="text-[9px] uppercase tracking-wider text-hg-green font-sans mb-1 inline-flex items-center gap-1">
                          <FileText size={10} /> Recruitment intake{l.intake.resubmitted ? ' · updated' : ''}
                          {l.intake.submitted_at && <span className="text-hg-text-dim normal-case tracking-normal"> · {new Date(l.intake.submitted_at).toLocaleDateString()}</span>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                          {INTAKE_FIELDS.filter(([k]) => l.intake[k]).map(([k, label]) => (
                            <div key={k} className="text-[11px] font-sans text-hg-text-dim leading-snug">
                              <span className="text-hg-text-dim/70">{label}:</span> <span className="text-hg-text">{l.intake[k]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      data-testid={`lead-resend-${l.id}`}
                      onClick={() => resend(l)}
                      disabled={busyId === l.id}
                      title="Re-send this lead to the fitter"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40"
                    >{busyId === l.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Resend</button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-hg-border/50 flex-wrap">
                  <span className="text-[9px] uppercase tracking-wider text-hg-text-dim font-sans mr-1">Follow-up:</span>
                  {FOLLOWUP_ORDER.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(l, s)}
                      disabled={busyId === l.id || l.follow_up_status === s}
                      className={`px-2 py-0.5 text-[10px] font-sans rounded-sm border transition-colors ${
                        l.follow_up_status === s ? FOLLOWUP[s].color : 'text-hg-text-dim border-hg-border hover:border-hg-green hover:text-hg-green'
                      } disabled:cursor-default`}
                    >{FOLLOWUP[s].label}</button>
                  ))}
                  {l.status_updated_at && (
                    <span className="text-[9px] font-sans text-hg-text-dim ml-1" data-testid={`lead-status-meta-${l.id}`}>
                      {l.status_source === 'fitter_email' ? 'set by fitter' : 'set on dashboard'}
                      {' · '}{new Date(l.status_updated_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[10px] font-sans text-hg-text-dim italic border-t border-hg-border pt-3">
        Leads are captured from Meta lead ads, stored privately in the Hub, and emailed to the fitter's routing addresses automatically. Contact details are only ever shown here (behind sign-in), never on a public page.
      </div>
    </div>
  );
}


// ===========================================================================
// Phase D — performance dashboard card
// ===========================================================================

function MetricTile({ icon: Icon, label, value, sub, tone }) {
  const valueColor = tone === 'green' ? 'text-hg-green' : tone === 'muted' ? 'text-hg-text-dim' : 'text-hg-text';
  return (
    <div className="bg-hg-bg border border-hg-border rounded-sm p-2.5">
      <div className="flex items-center gap-1.5">
        <Icon size={11} className="text-hg-text-dim" strokeWidth={1.5} />
        <div className="text-[9px] uppercase tracking-[0.16em] text-hg-text-dim font-sans">{label}</div>
      </div>
      <div className={`font-display text-lg mt-0.5 ${valueColor}`}>{value}</div>
      {sub && <div className="text-[10px] text-hg-text-dim font-sans mt-0.5">{sub}</div>}
    </div>
  );
}

function PerfDashboard({ insights, loading, onRefresh }) {
  const m = insights?.metrics;
  const dash = insights?.dashboard_status || {};
  const badge = STATUS_BADGE[dash.status] || STATUS_BADGE.draft;
  const spend = m?.spend ?? 0;
  const cap = insights?.spend_cap_usd;
  const pct = cap ? Math.min(100, Math.round((spend / cap) * 100)) : null;
  const num = (v, d = 0) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
  const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const fetched = insights?.fetched_at ? new Date(insights.fetched_at).toLocaleTimeString() : null;

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="draft-perf-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
            <BarChart3 size={12} /> Performance
          </div>
          <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${badge.color}`}>{badge.label}</span>
          {insights?.end_date && (
            <span className="text-[10px] font-sans text-hg-text-dim inline-flex items-center gap-1" data-testid="perf-window">
              <Clock size={10} /> ends {String(insights.end_date).slice(0, 10)}
              {insights.days_remaining != null ? ` · ${insights.days_remaining}d left` : ''}
            </span>
          )}
          {dash.note && (
            <span className={`text-[10px] font-sans ${dash.flag ? 'text-red-400' : 'text-hg-text-dim'} inline-flex items-center gap-1`}>
              {dash.flag && <AlertTriangle size={10} />}{dash.note}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {fetched && <span className="text-[10px] font-sans text-hg-text-dim">{insights?.cached ? 'as of' : 'updated'} {fetched}</span>}
          <button
            data-testid="perf-refresh-btn"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40"
          >{loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh</button>
        </div>
      </div>

      {insights?.geo_delivery?.leaking && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="geo-leak-warning">
          <AlertTriangle size={13} className="text-red-400 mt-0.5 flex-shrink-0" />
          <div className="text-[11px] font-sans text-red-300 leading-relaxed">
            <b>Geo leak — spend going out of area.</b> Only {insights.geo_delivery.in_state_pct}% of impressions are in {insights.geo_delivery.target_state} (target).
            {' '}{money(insights.geo_delivery.out_of_state_spend)} of {money(insights.geo_delivery.total_spend)} spent outside {insights.geo_delivery.target_state}.
            {insights.geo_delivery.top_out_of_state?.length > 0 && (
              <span className="text-red-400/80"> Top leak: {insights.geo_delivery.top_out_of_state.slice(0, 3).map((o) => o.region).join(', ')}.</span>
            )}
            {' '}Check the ad set's geo targeting (should be a local radius, not the whole US).
          </div>
        </div>
      )}
      {insights?.geo_delivery && !insights.geo_delivery.leaking && (
        <div className="text-[10px] font-sans text-hg-green inline-flex items-center gap-1" data-testid="geo-ok">
          <CheckCircle2 size={10} /> {insights.geo_delivery.in_state_pct}% of impressions in {insights.geo_delivery.target_state} — geo on target
        </div>
      )}

      {insights?.metrics_error ? (
        <div className="text-[11px] font-sans text-hg-text-dim italic">Insights unavailable: {insights.metrics_error}</div>
      ) : !m ? (
        <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-hg-green" /></div>
      ) : !m.has_data ? (
        <div className="text-[11px] font-sans text-hg-text-dim">No delivery data yet — this campaign hasn't spent or shown impressions. Metrics appear once it starts delivering.</div>
      ) : (
        <>
          {/* Spend vs cap */}
          <div className="bg-hg-bg border border-hg-border rounded-sm p-3 space-y-1.5">
            <div className="flex items-center justify-between text-[11px] font-sans">
              <span className="text-hg-text-dim inline-flex items-center gap-1"><DollarSign size={11} /> Spend</span>
              <span className="text-hg-text">
                <b className="text-hg-green">{money(spend)}</b>
                {cap != null ? <span className="text-hg-text-dim"> of {money(cap)} max</span> : <span className="text-hg-text-dim"> · no end-date cap</span>}
              </span>
            </div>
            {pct != null && (
              <div className="h-1.5 w-full bg-hg-border rounded-full overflow-hidden">
                <div className="h-full bg-hg-green rounded-full" style={{ width: `${pct}%` }} />
              </div>
            )}
            <div className="flex items-center justify-between text-[10px] font-sans text-hg-text-dim">
              <span>{insights?.daily_usd != null ? `${money(insights.daily_usd)}/day` : ''}</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={10} />
                {insights?.days_remaining != null ? `${insights.days_remaining} day${insights.days_remaining === 1 ? '' : 's'} left` : 'no end date'}
              </span>
            </div>
          </div>

          {/* Metric grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricTile icon={Eye} label="Impressions" value={num(m.impressions)} sub={`${num(m.reach)} reached`} />
            <MetricTile icon={Users} label="Frequency" value={num(m.frequency, 2)} sub="avg views/person" />
            <MetricTile icon={MousePointerClick} label="Clicks" value={num(m.clicks)} sub={`${num(m.ctr, 2)}% CTR`} />
            <MetricTile icon={Activity} label="CPC" value={money(m.cpc)} sub={`${money(m.cpm)} CPM`} />
          </div>

          {/* Leads — headline metric + Phase C note */}
          <div className="bg-hg-green/5 border border-hg-green/30 rounded-sm p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-hg-text-dim font-sans inline-flex items-center gap-1"><Target size={11} /> Leads</div>
                  <div className="font-display text-2xl text-hg-green mt-0.5">{num(m.leads)}</div>
                </div>
                <div className="border-l border-hg-border pl-3">
                  <div className="text-[9px] uppercase tracking-[0.16em] text-hg-text-dim font-sans">Cost / lead</div>
                  <div className="font-display text-2xl text-hg-text mt-0.5">{m.leads > 0 ? money(m.cpl) : '—'}</div>
                </div>
              </div>
            </div>
            <div className="text-[10px] font-sans text-hg-text-dim italic mt-2 inline-flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 flex-shrink-0 text-yellow-500" />
              Lead count from Meta insights. Retrieving each lead's contact details (name / email / phone) and routing them to the fitter is <b>Phase C — contact routing coming soon</b>.
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function DetailCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
      <div className="flex items-center gap-1.5">
        <Icon size={11} className="text-hg-text-dim" strokeWidth={1.5} />
        <div className="text-[9px] uppercase tracking-[0.18em] text-hg-text-dim font-sans">{label}</div>
      </div>
      <div className="font-display text-base text-hg-text mt-1">{value}</div>
      {sub && <div className="text-[10px] text-hg-text-dim font-sans truncate mt-0.5" title={sub}>{sub}</div>}
    </div>
  );
}
